// KAN DENNA TAS BORT? INNEHÅLLER ALLT I WeekSchedulePage.jsx NU?
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    Chip,
    Grid,
    MenuItem,
    TextField,
    Typography,
    Tabs,
    Tab,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { tokens } from "../assets/theme";
import {
    useWeekTemplates,
    useWeekTemplate,
    useUpdateWeekTemplate,
    useMachines,
    useAssignTemplate,
} from "../hooks/useWeekSchedule";
import WeekTemplateEditor from "../components/WeekTemplateEditor";

// ----- Tab 1: Veckoscheman -----
function WeekTemplatesTab({ companyKey }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const { data: templates = [] } = useWeekTemplates(companyKey);
    const { data: template } = useWeekTemplate(
        typeof selectedTemplateId === "number" ? selectedTemplateId : null
    );
    const updateTemplateMutation = useUpdateWeekTemplate();

    const [localTemplate, setLocalTemplate] = useState(null);

    useEffect(() => {
        if (template) {
            setLocalTemplate(JSON.parse(JSON.stringify(template)));
        } else {
            setLocalTemplate(null);
        }
    }, [template]);

    const workspaceBg =
        theme.palette.mode === "dark"
            ? colors.primary[700]
            : colors.grey[100];

    const handleSave = async () => {
        if (!localTemplate || !localTemplate.id) return;

        await updateTemplateMutation.mutateAsync({
            id: localTemplate.id,
            template: localTemplate,
        });
    };

    return (
        <Box
            sx={{
                mt: 1.5,
                p: 2,
                borderRadius: 2,
                backgroundColor: workspaceBg,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography variant="h5" gutterBottom>
                Veckoscheman
            </Typography>

            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={4}>
                    <TextField
                        select
                        fullWidth
                        label="Veckoschema"
                        value={selectedTemplateId}
                        onChange={(e) =>
                            setSelectedTemplateId(
                                e.target.value === "" ? "" : Number(e.target.value)
                            )
                        }
                    >
                        <MenuItem value="">
                            <em>Välj schema</em>
                        </MenuItem>
                        {templates.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                {localTemplate && (
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Namn på schema"
                            value={localTemplate.name || ""}
                            onChange={(e) =>
                                setLocalTemplate({
                                    ...localTemplate,
                                    name: e.target.value,
                                })
                            }
                        />
                    </Grid>
                )}
            </Grid>

            {localTemplate ? (
                <>
                    <WeekTemplateEditor
                        template={localTemplate}
                        onTemplateChange={setLocalTemplate}
                    />

                    <Box mt={3}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={
                                !localTemplate.id || updateTemplateMutation.isPending
                            }
                        >
                            Spara schema
                        </Button>
                    </Box>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Välj ett veckoschema för att redigera skift och namn.
                </Typography>
            )}
        </Box>
    );
}

// ----- Tab 2: Koppla schema till maskiner -----
function AssignToMachinesTab({ companyKey }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [selectedMachineIds, setSelectedMachineIds] = useState([]);
    const [weekStart, setWeekStart] = useState(
        dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD")
    );
    const [validTo, setValidTo] = useState("");

    const { data: templates = [] } = useWeekTemplates(companyKey);
    const { data: machines = [] } = useMachines(companyKey);
    const assignTemplateMutation = useAssignTemplate();

    const workspaceBg =
        theme.palette.mode === "dark"
            ? colors.primary[700]
            : colors.grey[100];

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                  "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                  },
              }
            : {};

    const weekEndDefault = useMemo(
        () => dayjs(weekStart).add(6, "day").format("YYYY-MM-DD"),
        [weekStart]
    );

    const handleAssign = async () => {
        if (!selectedTemplateId || selectedMachineIds.length === 0) return;

        const vf = dayjs(weekStart).startOf("day").toISOString();
        const vt = validTo ? dayjs(validTo).endOf("day").toISOString() : null;

        for (const machineId of selectedMachineIds) {
            await assignTemplateMutation.mutateAsync({
                machineId: machineId,
                weekTemplateId: Number(selectedTemplateId),
                validFrom: vf,
                validTo: vt,
            });
        }
    };

    return (
        <Box
            sx={{
                mt: 1.5,
                p: 2,
                borderRadius: 2,
                backgroundColor: workspaceBg,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography variant="h5" gutterBottom>
                Koppla veckoschema till maskiner
            </Typography>

            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Vecka start (måndag)"
                        InputLabelProps={{ shrink: true }}
                        value={weekStart}
                        onChange={(e) => setWeekStart(e.target.value)}
                        sx={dateTimeIconSx}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Giltig t.o.m."
                        InputLabelProps={{ shrink: true }}
                        value={validTo || ""}
                        placeholder={weekEndDefault}
                        onChange={(e) => setValidTo(e.target.value)}
                        sx={dateTimeIconSx}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        select
                        fullWidth
                        label="Veckoschema"
                        value={selectedTemplateId}
                        onChange={(e) =>
                            setSelectedTemplateId(
                                e.target.value === "" ? "" : Number(e.target.value)
                            )
                        }
                    >
                        <MenuItem value="">
                            <em>Välj schema</em>
                        </MenuItem>
                        {templates.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        select
                        fullWidth
                        label="Maskiner"
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((id) => {
                                        const m = machines.find((x) => x.id === id);
                                        return (
                                            <Chip
                                                key={id}
                                                label={m ? m.machineName : id}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            ),
                        }}
                        value={selectedMachineIds}
                        onChange={(e) =>
                            setSelectedMachineIds(
                                typeof e.target.value === "string"
                                    ? e.target.value.split(",").map(Number)
                                    : e.target.value
                            )
                        }
                    >
                        {machines.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                {m.machineName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>

            <Box mt={2}>
                <Button
                    variant="contained"
                    onClick={handleAssign}
                    disabled={
                        !selectedTemplateId ||
                        selectedMachineIds.length === 0 ||
                        assignTemplateMutation.isPending
                    }
                >
                    Koppla schema till maskiner
                </Button>
            </Box>
        </Box>
    );
}

// ----- Wrapper med flikar -----
function WeekSchedulePage() {
    const [activeTab, setActiveTab] = useState(0);
    const companyKey = 1; // byt till dynamiskt vid behov

    const handleTabChange = (_event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Box p={2} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography variant="h5" gutterBottom>
                Planeringsschema
            </Typography>

            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{ mb: 1 }}
            >
                <Tab label="Veckoscheman" />
                <Tab label="Koppla till maskiner" />
            </Tabs>

            {activeTab === 0 && <WeekTemplatesTab companyKey={companyKey} />}
            {activeTab === 1 && <AssignToMachinesTab companyKey={companyKey} />}
        </Box>
    );
}

export default WeekSchedulePage;
